import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { fromMinor, money, toMinor } from "@/lib/format";
import { useSession } from "@/lib/session";
import { GROUP_LABEL, atLeast, type Group, type ShopCustomer } from "@/lib/types";
import { Button, Field, Input, Modal, Select, cx, useToast } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";

const GROUPS: Group[] = ["retail", "wholesale", "distributor", "staff"];

/** Shop customers: their group sets which prices they get at the till, and any on-account limit. */
export function Customers() {
  const s = useSession();
  const manager = atLeast(s.staff?.role, "manager");
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<Group | "">("");
  const [edit, setEdit] = useState<ShopCustomer | null>(null);
  const [paying, setPaying] = useState<ShopCustomer | null>(null);
  const list = useQuery({
    queryKey: ["admin-customers", q, group],
    queryFn: () => api<{ customers: ShopCustomer[]; groups: Record<string, number> }>(till(`/admin/customers?q=${encodeURIComponent(q)}&group=${group}`)),
  });
  const counts = list.data?.groups ?? {};
  return (
    <>
      <PageHead title="Customers & groups" sub="Customers who give a phone number at the till are added here. Put a trader in Wholesale or Distributor and the till charges them those prices." />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button tone={group === "" ? "dark" : "light"} onClick={() => setGroup("")}>All</Button>
          {GROUPS.map((g) => (
            <Button key={g} tone={group === g ? "dark" : "light"} onClick={() => setGroup(g)}>
              {GROUP_LABEL[g]} · <span className="font-mono">{counts[g] ?? 0}</span>
            </Button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" className="ml-auto !w-64" />
        </div>
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={th}>Customer</th>
                  <th className={th}>Phone</th>
                  <th className={th}>Group</th>
                  <th className={th}>On account</th>
                  <th className={th}>Owes</th>
                  <th className={th}>Note</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {(list.data?.customers ?? []).map((c) => (
                  <tr key={c.id}>
                    <td className={td}><b>{c.name || "No name"}</b>{c.email ? <span className="block text-xs text-slate-500">{c.email}</span> : null}</td>
                    <td className={cx(td, "font-mono")}>{c.phone}</td>
                    <td className={td}><span className={cx("px-2 py-0.5 text-[11px] font-extrabold uppercase", c.group === "retail" ? "bg-slate-100 text-slate-600" : c.group === "distributor" ? "bg-[#3b2a7a] text-white" : "bg-brand-ink text-white")}>{GROUP_LABEL[c.group]}</span></td>
                    <td className={cx(td, "font-mono")}>{c.creditLimit ? `up to ${money(c.creditLimit)}` : "No"}</td>
                    <td className={cx(td, "font-mono", (c.balance ?? 0) > 0 && "font-bold")}>{(c.balance ?? 0) > 0 ? money(c.balance!) : "-"}</td>
                    <td className={cx(td, "max-w-[260px] truncate text-slate-500")}>{c.note ?? ""}</td>
                    <td className={td}>
                      <span className="flex gap-1.5">
                        {(c.balance ?? 0) > 0 ? <Button size="sm" tone="primary" onClick={() => setPaying(c)}>Take payment</Button> : null}
                        {manager ? <Button size="sm" onClick={() => setEdit(c)}>Edit</Button> : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.data && !list.data.customers.length ? <p className="px-5 py-6 text-sm text-slate-500">No customers yet. They're added when the cashier attaches a phone number to a sale.</p> : null}
          </div>
        </Card>
      </div>
      {edit ? <EditCustomer c={edit} onClose={() => setEdit(null)} /> : null}
      {paying ? <TakePayment c={paying} onClose={() => setPaying(null)} /> : null}
    </>
  );
}

function EditCustomer({ c, onClose }: { c: ShopCustomer; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: c.name ?? "", email: c.email ?? "", group: c.group, credit: c.creditLimit != null ? fromMinor(c.creditLimit) : "", terms: String(c.termsDays ?? 30), note: c.note ?? "" });
  const [buyers, setBuyers] = useState((c.buyers ?? []).map((b) => ({ name: b.name, limit: b.limit != null ? fromMinor(b.limit) : "" })));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api(till(`/admin/customers/${c.id}`), {
        method: "PUT",
        body: {
          name: f.name, email: f.email, group: f.group, creditLimit: f.credit === "" ? null : toMinor(f.credit), note: f.note,
          termsDays: f.terms === "" ? null : Number(f.terms),
          buyers: buyers.filter((b) => b.name.trim()).map((b) => ({ name: b.name.trim(), limit: b.limit === "" ? null : toMinor(b.limit) })),
        },
      });
      toast("Saved");
      void qc.invalidateQueries({ queryKey: ["admin-customers"] });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={c.name || c.phone} sub={c.phone} onClose={onClose} footer={<Button tone="primary" disabled={busy} onClick={() => void save()}>Save</Button>}>
      <div className="grid gap-3">
        <Field label="Group" hint="Sets which prices the till charges them (Settings › Wholesale)">
          <Select value={f.group} onChange={(e) => setF({ ...f, group: e.target.value as Group })}>
            {GROUPS.map((g) => <option key={g} value={g}>{GROUP_LABEL[g]}</option>)}
          </Select>
        </Field>
        <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Can owe up to (on account)" hint="Blank = pays in full every time">
          <Input inputMode="decimal" value={f.credit} onChange={(e) => setF({ ...f, credit: e.target.value })} className="font-mono" />
        </Field>
        {f.credit !== "" ? (
          <>
            <Field label="Days to pay" hint="Shown as the due date at the till and on the account">
              <Input inputMode="numeric" value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value.replace(/\D/g, "") })} />
            </Field>
            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-semibold">Who may buy on the account</span>
              <span className="text-xs text-slate-500">Leave empty to let anyone, with a supervisor's PIN. Named here, only these people can, each up to their own amount per sale.</span>
              {buyers.map((b, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                  <Input aria-label="Name" value={b.name} onChange={(e) => setBuyers(buyers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Ato Quaye (site foreman)" className="!w-56" />
                  up to <Input inputMode="decimal" aria-label="Most per sale" value={b.limit} onChange={(e) => setBuyers(buyers.map((x, j) => (j === i ? { ...x, limit: e.target.value } : x)))} placeholder="any" className="!w-28 font-mono" /> a sale
                  <Button size="sm" onClick={() => setBuyers(buyers.filter((_, j) => j !== i))}>Remove</Button>
                </div>
              ))}
              {buyers.length < 12 ? <Button size="sm" className="self-start" onClick={() => setBuyers([...buyers, { name: "", limit: "" }])}>+ Add a person</Button> : null}
            </div>
          </>
        ) : null}
        <Field label="Note"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={300} /></Field>
      </div>
    </Modal>
  );
}

/** A customer pays toward their account. Cash goes into this till's drawer. */
function TakePayment({ c, onClose }: { c: ShopCustomer; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [amount, setAmount] = useState(fromMinor(c.balance ?? 0));
  const [method, setMethod] = useState("momo");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ paid: number; balance: number }>(till(`/customers/${c.id}/payments`), { body: { amount: toMinor(amount), method, reference: ref } });
      toast(`Took ${money(r.paid)}. ${r.balance > 0 ? `Still owes ${money(r.balance)}` : "Account cleared"}`);
      void qc.invalidateQueries({ queryKey: ["admin-customers"] });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't record it", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`${c.name || c.phone} pays toward their account`} sub={`Owes ${money(c.balance ?? 0)}. Paid against the oldest sale first.`} onClose={onClose} footer={<Button tone="primary" disabled={busy || !amount} onClick={() => void save()}>Record payment</Button>}>
      <div className="grid gap-3">
        <Field label="Amount"><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono text-lg" autoFocus /></Field>
        <Field label="Paid by" hint="Cash is taken at a till, so it goes in that till's drawer">
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="momo">Mobile Money</option>
            <option value="card">Card</option>
          </Select>
        </Field>
        {method !== "cash" ? <Field label={method === "card" ? "Approval code" : "Mobile Money reference"}><Input value={ref} onChange={(e) => setRef(e.target.value)} className="font-mono" /></Field> : null}
      </div>
    </Modal>
  );
}
